import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Check, Loader2, X } from 'lucide-react';
import type { RefinementDiffEntry, RefinementProposalResponse } from '../../types/calibration-api.js';

interface RefinementDiffCardProps {
  /**
   * The refinement-proposal route's response, whole and unmodified.
   *
   * Deliberately the entire response rather than a `proposal` prop plus a few
   * loose extras: `appliedDiff`, `droppedDiffEntries` and `droppedReason` are
   * what make the applied state honest, and a caller that can forget to pass
   * them is a caller that can render "Applied — here's what changed" over a
   * change that was never applied. The type is the mirrored server contract,
   * so a field the server stops sending is a compile error here.
   *
   * `proposal.status` is untrusted and load-bearing: the GET route falls back
   * to the most recently RESOLVED proposal when nothing is pending, so this
   * component is routinely handed an already-decided row. Only the literal
   * 'pending' is actionable — anything else, including a value this client
   * doesn't recognise, renders read-only. The production bug (2026-08-02) was
   * a resolved proposal rendering live Accept/Reject, which made a successful
   * accept look dead and sent the recruiter into a silent 404 on the second
   * click.
   */
  data: RefinementProposalResponse;
  onAccept: (proposalId: number) => void;
  onReject: (proposalId: number) => void;
  /** Where to send the recruiter to SEE the accepted change take effect. */
  suggestedHref?: string;
  /** True while an accept/reject is in flight — blocks the double-submit path. */
  isSubmitting?: boolean;
  /** Non-null when the last accept/reject failed. Must be visible, never swallowed. */
  errorMessage?: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  mustHaveTerms: 'Must have',
  niceToHaveTerms: 'Nice to have',
  exclusionTerms: 'Exclude',
};

function DiffList({
  entries,
  testId,
  muted = false,
}: {
  entries: RefinementDiffEntry[];
  testId?: string;
  muted?: boolean;
}) {
  return (
    <ul className="space-y-1" data-testid={testId}>
      {entries.map((entry, i) => (
        <li
          key={i}
          className={
            muted
              ? 'text-sm text-gray-500 line-through dark:text-gray-400'
              : 'text-sm text-gray-700 dark:text-gray-300'
          }
        >
          <span
            className={
              entry.action === 'add'
                ? 'font-mono text-green-700 dark:text-green-400'
                : 'font-mono text-red-700 dark:text-red-400'
            }
          >
            {entry.action === 'add' ? '+' : '−'}
          </span>{' '}
          {FIELD_LABELS[entry.field] ?? entry.field}:{' '}
          <span
            className={
              muted
                ? 'font-medium text-gray-600 dark:text-gray-300'
                : 'font-medium text-gray-900 dark:text-gray-100'
            }
          >
            {entry.term}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * REVIEW FINDING M7. The server drops exclusion terms that aren't role-shaped
 * (`exclusion-term-validation.ts`) — a bare "UX" is matched literally against
 * resume text and would demote 11 real people on job 147's pool alone. The
 * drop was already happening; nothing rendered it, so the recruiter was shown
 * the model's request as if it were the outcome.
 *
 * `heading`/`reason` come from the caller and the SERVER respectively: the
 * client states WHEN the drop happens (before vs. after the click) because
 * only the client knows which state it's in, and quotes the server verbatim
 * for WHY, because only the server owns the rule.
 */
function DroppedNotice({
  entries,
  reason,
  heading,
}: {
  entries: RefinementDiffEntry[];
  reason: string | null;
  heading: string;
}) {
  if (entries.length === 0) return null;
  return (
    <div
      data-testid="dropped-diff"
      className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950"
    >
      <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
        <span>{heading}</span>
      </p>
      <DiffList entries={entries} muted />
      {reason && <p className="text-sm text-amber-900 dark:text-amber-200">{reason}</p>}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="p-4 sm:p-6 space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {children}
      </div>
    </div>
  );
}

export function RefinementDiffCard({
  data,
  onAccept,
  onReject,
  suggestedHref,
  isSubmitting = false,
  errorMessage,
}: RefinementDiffCardProps) {
  const { proposal, appliedDiff, droppedDiffEntries, droppedReason } = data;
  const isPending = proposal.status === 'pending';

  if (!isPending) {
    const accepted = proposal.status === 'accepted';
    return (
      <Shell>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {accepted ? (
            <>
              <Check size={18} className="text-green-600 dark:text-green-400" aria-hidden />
              Applied — here&apos;s what changed
            </>
          ) : (
            <>
              <X size={18} className="text-gray-500 dark:text-gray-400" aria-hidden />
              Rejected — the search was left unchanged
            </>
          )}
        </h2>
        <p className="text-sm text-gray-700 dark:text-gray-300">{proposal.reasoning}</p>
        {/*
          Applied state lists `appliedDiff`, NOT `proposedDiff`. On a rejected
          proposal nothing was applied and the distinction is moot, so the same
          list serves both — it is still the accurate account of what an accept
          would have done.
        */}
        <DiffList entries={appliedDiff} testId="applied-diff" />
        {accepted && (
          <DroppedNotice
            entries={droppedDiffEntries}
            reason={droppedReason}
            heading="Not applied — Arbie left these out"
          />
        )}
        {accepted && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Arbie is now searching with these criteria. New candidates are ranked against them from here on.
          </p>
        )}
        {accepted && suggestedHref && (
          <Link
            to={suggestedHref}
            className="inline-flex items-center gap-1 text-sm font-medium text-accent-700 hover:text-accent-800 dark:text-accent-400 dark:hover:text-accent-300"
          >
            See the updated suggested candidates
            <ArrowRight size={14} aria-hidden />
          </Link>
        )}
      </Shell>
    );
  }

  /**
   * REVIEW FINDING H1. The filter can drop EVERY entry — US-495's head-word
   * rule makes that materially likely (a lone `{exclusionTerms, add, "B2B
   * SaaS"}` is dropped outright; production proposal id 1 was already 50%
   * dropped). `DiffList` renders an empty <ul> for an empty array, so the card
   * previously showed a live Accept button over nothing, and accepting wrote a
   * `job_search_specs` version byte-identical to the one it replaced.
   *
   * The server now refuses that write and resolves the proposal as rejected.
   * The button must go with it: an action that cannot change anything is a
   * dead button, which is the exact walkthrough complaint (item 7) this epic
   * family keeps re-earning. What remains is Dismiss — the same reject call,
   * named for what it does here.
   */
  if (appliedDiff.length === 0) {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Arbie had an idea, but there&apos;s nothing safe to apply
        </h2>
        <p className="text-sm text-gray-700 dark:text-gray-300">{proposal.reasoning}</p>
        <DroppedNotice
          entries={droppedDiffEntries}
          reason={droppedReason}
          heading="Arbie suggested this, and it was left out"
        />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Nothing here would change your search, so there is nothing to accept. Your judgments were
          still saved, and they feed the next round.
        </p>
        {errorMessage && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
            <span>{errorMessage}</span>
          </p>
        )}
        <button
          type="button"
          disabled={isSubmitting}
          className="px-4 py-2 rounded bg-gray-200 text-gray-900 hover:bg-gray-300 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          onClick={() => onReject(proposal.id)}
        >
          Dismiss
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Arbie wants to update the search
      </h2>
      <p className="text-sm text-gray-700 dark:text-gray-300">{proposal.reasoning}</p>
      {/*
        The pending card shows what accepting will ACTUALLY do, for the same
        reason the accepted card does: a diff the recruiter approves and a diff
        the server writes have to be the same diff. The dropped entries are
        shown alongside, in the future tense — deciding with the whole picture
        beats discovering the omission afterwards.
      */}
      <DiffList entries={appliedDiff} testId="applied-diff" />
      <DroppedNotice
        entries={droppedDiffEntries}
        reason={droppedReason}
        heading="These won't be applied if you accept"
      />
      {errorMessage && (
        <p
          role="alert"
          className="flex items-start gap-2 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>{errorMessage}</span>
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-green-600 dark:hover:bg-green-500"
          onClick={() => onAccept(proposal.id)}
        >
          {isSubmitting && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Accept
        </button>
        <button
          type="button"
          disabled={isSubmitting}
          className="px-4 py-2 rounded bg-gray-200 text-gray-900 hover:bg-gray-300 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          onClick={() => onReject(proposal.id)}
        >
          Reject
        </button>
      </div>
    </Shell>
  );
}
